import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Check, X } from 'lucide-react';
interface MultiSelectDropdownProps {
  label: string;
  options: string[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
  otherValue?: string;
  onOtherChange?: (value: string) => void;
}
export function MultiSelectDropdown({
  label,
  options,
  selected,
  onChange,
  placeholder = 'Select options...',
  otherValue,
  onOtherChange
}: MultiSelectDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const toggleOption = (option: string) => {
    if (selected.includes(option)) {
      onChange(selected.filter(item => item !== option));
    } else {
      onChange([...selected, option]);
    }
  };
  const removeOption = (option: string) => {
    onChange(selected.filter(item => item !== option));
  };
  const hasOther = options.includes('Other');
  const showOtherInput = hasOther && selected.includes('Other');
  return <div data-id="element-1363">
      <label className="block text-[#1A3D3A] mb-3" style={{
      fontFamily: "'DM Sans', sans-serif",
      fontSize: '16px',
      fontWeight: 500
    }} data-id="element-1364">
        {label}
      </label>

      <div ref={dropdownRef} className="relative" data-id="element-1365">
        {/* Selected items display */}
        <div onClick={() => setIsOpen(!isOpen)} className="min-h-[48px] px-4 py-2 border-2 border-[#1A3D3A]/20 rounded-lg cursor-pointer hover:border-[#1A3D3A]/40 transition-colors flex items-center justify-between gap-2" data-id="element-1366">
          <div className="flex-1 flex flex-wrap gap-2" data-id="element-1367">
            {selected.length === 0 ? <span className="text-[#3D5654]/50" style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: '15px'
          }} data-id="element-1368">
                {placeholder}
              </span> : selected.map(item => <span key={item} className="inline-flex items-center gap-1 px-2 py-1 bg-[#1A3D3A]/10 rounded text-[#1A3D3A] text-sm" style={{
            fontFamily: "'DM Sans', sans-serif"
          }} data-id="element-1369">
                  {item}
                  <button onClick={e => {
              e.stopPropagation();
              removeOption(item);
            }} className="hover:bg-[#1A3D3A]/20 rounded-full p-0.5 transition-colors" data-id="element-1370">
                    <X className="w-3 h-3" data-id="element-1371" />
                  </button>
                </span>)}
          </div>
          <ChevronDown className={`w-5 h-5 text-[#3D5654] transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} data-id="element-1372" />
        </div>

        {/* Dropdown menu */}
        <AnimatePresence data-id="element-1373">
          {isOpen && <motion.div initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.15
        }} className="absolute z-10 w-full mt-2 bg-white border-2 border-[#1A3D3A]/20 rounded-lg shadow-lg max-h-64 overflow-y-auto" data-id="element-1374">
              {options.map(option => <button key={option} onClick={() => toggleOption(option)} className="w-full px-4 py-3 text-left hover:bg-[#F8FAF9] transition-colors flex items-center justify-between gap-3" style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: '15px'
          }} data-id="element-1375">
                  <span className="text-[#1A3D3A]" data-id="element-1376">{option}</span>
                  {selected.includes(option) && <Check className="w-5 h-5 text-[#1A3D3A] flex-shrink-0" data-id="element-1377" />}
                </button>)}
            </motion.div>}
        </AnimatePresence>
      </div>

      {/* Other input */}
      {showOtherInput && onOtherChange && <motion.div initial={{
      opacity: 0,
      height: 0
    }} animate={{
      opacity: 1,
      height: 'auto'
    }} className="mt-3" data-id="element-1378">
          <input type="text" value={otherValue || ''} onChange={e => onOtherChange(e.target.value)} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none" style={{
        fontFamily: "'DM Sans', sans-serif"
      }} placeholder="Please specify..." data-id="element-1379" />
        </motion.div>}

      {selected.length > 0 && <p className="text-[#3D5654] text-sm mt-2" style={{
      fontFamily: "'DM Sans', sans-serif"
    }} data-id="element-1380">
          {selected.length} selected
        </p>}
    </div>;
}